import React from 'react';
import { Link } from 'react-router-dom';
import { captureOutboundLinkClick } from '../utils/analytics';
import Glp1BridgeResourceNav from './Glp1BridgeResourceNav';
import TapNativeAdUnit from './TapNativeAdUnit';

const executiveSummary = [
  {
    title: 'What it is',
    body: 'Medicare is covering Foundayo, Wegovy, and Zepbound (KwikPen® only) for weight loss at a flat $50 copay/month from July 1, 2026 through December 31, 2027.',
  },
  {
    title: 'Why it matters for patients',
    body: 'GLP-1s for obesity have had no Part D coverage. The Bridge changes that for eligible Medicare patients, including SNP and EGWP enrollees.',
  },
  {
    title: 'How it works in practice',
    body: 'Confirm Bridge routing, send the prescription with Bridge instructions, have the pharmacy submit the Bridge claim, then complete the PA request after the Bridge claim rejects for PA.',
  },
  {
    title: 'What to do now',
    body: 'Identify eligible patients, document BMI at therapy initiation, include E66 diagnosis coding when appropriate, and prepare prescriber/pharmacy teams for the July 2026 workflow.',
  },
];

const programDetails = [
  { detail: 'Program Dates',        answer: 'July 1, 2026 – December 31, 2027' },
  { detail: 'Availability',         answer: 'Nationwide — available in all states and territories' },
  { detail: 'Eligible Drugs',       answer: 'Foundayo® (all formulations), Wegovy® (all formulations), Zepbound® (KwikPen® only) for weight loss only' },
  { detail: 'Patient Copay',        answer: '$50/month flat — does not count toward Part D TrOOP' },
  { detail: 'Manufacturer Net Price', answer: '$245 per monthly supply under the Bridge' },
  { detail: 'PA Submitted To',      answer: 'CMS central processor, not the patient plan' },
  { detail: 'PA Prerequisite',      answer: 'Denied pharmacy claim to Bridge BIN 028918 / PCN MEDDGLP1BR before PA submission' },
  { detail: 'Central Processor',    answer: 'Humana, using LI NET infrastructure' },
  { detail: 'Pharmacy BIN/PCN',     answer: '028918 / MEDDGLP1BR' },
  { detail: 'Processor / Route',    answer: 'SS&C Health — claims routed through RelayHealth' },
  { detail: 'Pharmacy Help Desk',   answer: '844-673-0910' },
  { detail: 'Plan Opt-In Required?', answer: 'No. Part D plans are not involved.' },
  { detail: 'Low-Income Subsidy Applies?', answer: 'No. LIS/Extra Help copay does not apply under the Bridge.' },
  { detail: 'Medicare Prescription Payment Plan?', answer: 'No. Bridge fills are outside Part D and are not eligible for M3P.' },
];

const ineligiblePlans = [
  'Private fee-for-service (PFFS) plans',
  'Section 1876 cost contract plans',
  'Section 1833 health care prepayment plans (HCPPs)',
  'PACE organizations',
  'Religious fraternal benefit plans',
  'Fallback plans',
];

const paCriteria = [
  { threshold: 'BMI ≥ 35', condition: 'No comorbidity required' },
  { threshold: 'BMI ≥ 30', condition: 'HFpEF, or uncontrolled hypertension (SBP >140 or DBP >90 despite ≥2 antihypertensives), or CKD stage 3a or above' },
  { threshold: 'BMI ≥ 27', condition: 'Pre-diabetes (ADA guidelines), or previous MI, or previous stroke, or symptomatic peripheral artery disease' },
];

const ndcRows = [
  {
    drug: 'Foundayo®',
    coverage: 'All formulations',
    ndcs: '0002-4178-31, 0002-4503-31, 0002-4794-31, 0002-4803-31, 0002-4839-31, 0002-4953-31',
  },
  {
    drug: 'Wegovy®',
    coverage: 'All formulations',
    ndcs: '0169-4525-14, 0169-4505-14, 0169-4501-14, 0169-4517-14, 0169-4524-14, 0169-4415-31, 0169-4404-31, 0169-4409-31, 0169-4425-31, 0169-4572-14',
  },
  {
    drug: 'Zepbound®',
    coverage: 'KwikPen® only',
    ndcs: '0002-3566-11, 0002-3555-11, 0002-3544-11, 0002-3533-11, 0002-3522-11, 0002-3511-11',
  },
];

const oopImpact = [
  { category: 'Drug cost counts toward Part D GCPDC',  status: 'No' },
  { category: '$50 copay counts toward TrOOP',          status: 'No' },
  { category: 'Low-Income Subsidy (LIS) applies',       status: 'No' },
  { category: 'Copay varies by Part D benefit phase',   status: 'No — flat $50' },
  { category: 'Coupons or discount programs apply',     status: 'No' },
  { category: 'Medicare Prescription Payment Plan applies', status: 'No' },
];

const bridgeExtension = [
  'CMS says BALANCE will not launch in Medicare in 2027.',
  'The Bridge is extended through December 31, 2027 to maintain access and collect additional utilization data.',
  'CMS expects to share GLP-1 utilization data with Part D sponsors ahead of potential future BALANCE implementation in Part D.',
  'Ozempic; Rybelsus; Wegovy has a negotiated maximum fair price beginning January 1, 2027, but CMS says the Bridge is a distinct time-limited demonstration.',
  'CMS says the 2026 Bridge period operates independently from the MFP, and more information on the 2027 demonstration period is forthcoming.',
];

const actionItems = [
  'Check eligible Part D coverage, prior Part D GLP-1 coverage, and Part D-coverable diagnoses before Bridge routing.',
  'Write the prescription for weight management only and include an E66 diagnosis code plus Bridge instruction when appropriate.',
  'Wait for the denied Bridge pharmacy claim before submitting the Bridge PA.',
  'Submit PA to the CMS central processor, not the patient plan, for weight-loss-only use.',
  'Confirm indication routing: type 2 diabetes, MASH, OSA, and cardiovascular risk reduction uses still route through the patient plan.',
  'Advise patients about the flat $50 copay and no TrOOP accumulation impact.',
  'Keep the CMS payer sheet and reject-code tool available for pharmacy billing support.',
];

const workflowSteps = [
  {
    title: '1. Screen the route',
    body: 'Confirm eligible Part D coverage, no prior GLP-1 coverage through Part D, no Part D-coverable diagnosis, and weight-management-only use.',
  },
  {
    title: '2. Send the prescription',
    body: 'When Bridge may apply, CMS prescriber materials say to include an obesity diagnosis code from the E66 family and the instruction “SEND TO BRIDGE FOR WEIGHT MANAGEMENT.”',
  },
  {
    title: '3. Pharmacy submits the Bridge claim',
    body: 'The pharmacy routes the claim to BIN 028918 / PCN MEDDGLP1BR with the beneficiary Medicare number and the Bridge-covered product/NDC.',
  },
  {
    title: '4. PA request follows the rejected Bridge claim',
    body: 'CMS PA form says a denied pharmacy claim to Bridge BIN 028918 / PCN MEDDGLP1BR must exist before PA submission.',
  },
  {
    title: '5. Decision and fills',
    body: 'CMS materials state the PA decision is communicated within 72 hours of submission. After approval, subsequent fills generally do not need a new PA unless the patient switches covered GLP-1 drugs.',
  },
];

const exclusionItems = [
  'Received a GLP-1 through the patient’s Part D plan.',
  'Type 2 diabetes.',
  'Moderate-to-severe obstructive sleep apnea.',
  'Noncirrhotic metabolic dysfunction-associated steatohepatitis (MASH) with moderate to advanced liver scarring.',
  'Use to reduce risk of major adverse cardiovascular events in adults with established cardiovascular disease.',
];

const pharmacyTroubleshooting = [
  { code: '75', meaning: 'Prior authorization required', action: 'Initiate PA with the prescriber through ePA or fax.' },
  { code: '70', meaning: 'Submitted NDC not eligible', action: 'Rewrite or resubmit for an eligible Bridge product/NDC.' },
  { code: '65', meaning: 'Beneficiary eligibility failure', action: 'Verify MBI and plan/Bridge eligibility; patient may need CMS support.' },
  { code: '07 / CA / CB / 09 / 52', meaning: 'Missing or mismatched beneficiary data', action: 'Confirm MBI, patient name, and date of birth; use E1 lookup if needed.' },
  { code: '76', meaning: 'Plan limitation', action: 'Resubmit for a covered 28- or 30-day supply and appropriate package quantity.' },
  { code: '79', meaning: 'Refilled too soon', action: 'Resubmit after more than 75% of the prior day supply has been used.' },
  { code: 'RK', meaning: 'Partial fill not supported', action: 'Bridge does not support partial-fill transactions.' },
];

const sourceLinks = [
  {
    title: 'CMS Program Page',
    description: 'Official overview, FAQs, beneficiary information, and PA form link.',
    href: 'https://www.cms.gov/medicare/coverage/prescription-drug-coverage/medicare-glp-1-bridge',
  },
  {
    title: 'Information for Providers',
    description: 'Provider FAQs, clinical criteria, PA routing, and prescriber eligibility.',
    href: 'https://www.cms.gov/medicare/coverage/prescription-drug-coverage/medicare-glp-1-bridge/information-providers',
  },
  {
    title: 'Information for Pharmacies',
    description: 'Pharmacy workflow, claim routing, fill quantities, payment, and source files.',
    href: 'https://www.cms.gov/medicare/coverage/prescription-drug-coverage/medicare-glp-1-bridge/information-pharmacies',
  },
  {
    title: 'Information for Part D Plans',
    description: 'Part D sponsor expectations and interaction with Bridge routing.',
    href: 'https://www.cms.gov/medicare/coverage/prescription-drug-coverage/medicare-glp-1-bridge/information-part-d-plans',
  },
  {
    title: 'Prior Authorization Form',
    description: 'CMS Bridge PA form for prescriber submission after the Bridge claim prerequisite.',
    href: 'https://www.cms.gov/glp-1-bridge.pdf',
  },
  {
    title: 'Prescriber Fact Sheet',
    description: 'June 2026 prescriber workflow, prescription note guidance, and PA timing.',
    href: 'https://www.cms.gov/files/document/glp-1-prescribers-c-1.pdf',
  },
  {
    title: 'Pharmacy Fact Sheet',
    description: 'June 2026 pharmacy workflow, MBI lookup, PA transmission, and refill guidance.',
    href: 'https://www.cms.gov/files/document/glp-1-pharmacies-c.pdf',
  },
  {
    title: 'Payer Sheet',
    description: 'NCPDP billing fields and Bridge payer-route details.',
    href: 'https://www.cms.gov/files/document/glp-1-bridge-payer-sheet.pdf',
  },
  {
    title: 'Reject Code Reference Tool',
    description: 'CMS/NCPDP troubleshooting reference for Bridge claim rejects.',
    href: 'https://www.cms.gov/files/document/medicare-glp-1-bridge-pharmacy-reject-code-reference-tool.pdf',
  },
  {
    title: 'June 11 Pharmacy Webinar Slides',
    description: 'CMS pharmacy walkthrough slides; use as supporting operational guidance.',
    href: 'https://www.cms.gov/files/document/medicare-glp-1-bridge-pharmacywebinarslides-061126.pdf',
  },
];

/* ── Small reusable components ─────────────────── */

function SectionTitle({ children }) {
  return <h3 className="text-[22px] font-semibold text-[#1d1d1f] mb-4 mt-10">{children}</h3>;
}

function InfoCard({ children, className = '' }) {
  return (
    <div className={`rounded-lg border border-[#e5e5ea] bg-white p-5 ${className}`}>
      {children}
    </div>
  );
}

function AlertBox({ type = 'info', title, children }) {
  const styles = {
    info:    'bg-blue-50   border-blue-200   text-blue-800',
    warning: 'bg-amber-50  border-amber-200  text-amber-800',
    success: 'bg-green-50  border-green-200  text-green-800',
  };
  return (
    <div className={`rounded-lg border px-5 py-4 text-sm leading-relaxed ${styles[type]} mt-4`}>
      {title && <p className="font-semibold mb-1">{title}</p>}
      <p className="text-[14px] leading-relaxed opacity-90">{children}</p>
    </div>
  );
}

function Table2Col({ rows, col1, col2 }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-[#e5e5ea]">
      <table className="w-full text-sm text-left">
        <thead className="bg-[#f5f5f7] border-b border-[#e5e5ea]">
          <tr>
            <th className="px-5 py-3 font-semibold text-[#1d1d1f] w-[220px]">{col1}</th>
            <th className="px-5 py-3 font-semibold text-[#1d1d1f]">{col2}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-[#e5e5ea] last:border-0">
              <td className="px-5 py-3 font-medium text-[#1d1d1f] align-top">{row[0]}</td>
              <td className="px-5 py-3 text-[#3f3f46] align-top">{row[1]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NdcTable({ rows }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-[#e5e5ea]">
      <table className="w-full text-sm text-left">
        <thead className="bg-[#f5f5f7] border-b border-[#e5e5ea]">
          <tr>
            <th className="px-5 py-3 font-semibold text-[#1d1d1f] w-[150px]">Drug</th>
            <th className="px-5 py-3 font-semibold text-[#1d1d1f] w-[170px]">Bridge Coverage</th>
            <th className="px-5 py-3 font-semibold text-[#1d1d1f]">Published NDCs</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.drug} className="border-b border-[#e5e5ea] last:border-0">
              <td className="px-5 py-3 font-medium text-[#1d1d1f] align-top">{row.drug}</td>
              <td className="px-5 py-3 text-[#3f3f46] align-top">{row.coverage}</td>
              <td className="px-5 py-3 text-[#3f3f46] align-top">{row.ndcs}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RejectCodeTable({ rows }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-[#e5e5ea]">
      <table className="w-full text-sm text-left">
        <thead className="bg-[#f5f5f7] border-b border-[#e5e5ea]">
          <tr>
            <th className="px-5 py-3 font-semibold text-[#1d1d1f] w-[120px]">Code</th>
            <th className="px-5 py-3 font-semibold text-[#1d1d1f] w-[240px]">Meaning</th>
            <th className="px-5 py-3 font-semibold text-[#1d1d1f]">Common action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.code} className="border-b border-[#e5e5ea] last:border-0">
              <td className="px-5 py-3 font-semibold text-[#1d1d1f] align-top">{row.code}</td>
              <td className="px-5 py-3 text-[#3f3f46] align-top">{row.meaning}</td>
              <td className="px-5 py-3 text-[#3f3f46] align-top">{row.action}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SourceLinks({ links }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {links.map((link) => (
        <a
          key={link.href}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            captureOutboundLinkClick(
              `glp1-bridge-source-${link.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
              link.href,
              { placement: 'glp1-resource-page-source-grid' }
            )
          }
          className="block rounded-lg border border-[#e5e5ea] bg-white px-5 py-4 transition hover:border-[#b8c7d9] hover:bg-[#f8fbff]"
        >
          <p className="text-sm font-semibold text-[#1d1d1f]">{link.title}</p>
          <p className="mt-1 text-xs leading-relaxed text-[#6e6e73]">{link.description}</p>
          <span className="mt-3 inline-flex text-xs font-semibold text-[#06c]">Open source →</span>
        </a>
      ))}
    </div>
  );
}

/* ── Main component ────────────────────────────── */

export default function MedicareGlp1BridgePage() {
  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-[1120px] mx-auto px-6 md:px-10 py-16">
        <div className="grid gap-8 xl:grid-cols-[minmax(0,820px)_240px] xl:items-start">
          <article className="min-w-0">

        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-[#86868b] mb-10">
          <Link to="/resources" className="hover:text-[#1d1d1f] transition-colors">Resources</Link>
          <span>/</span>
          <Link to="/resources/glp1-bridge" className="hover:text-[#1d1d1f] transition-colors">Medicare GLP-1 Bridge</Link>
          <span>/</span>
          <span className="text-[#1d1d1f]">Guide</span>
        </div>

        {/* Header */}
        <div className="mb-10 pb-8 border-b border-[#e5e5ea]">
          <span className="inline-block text-xs font-semibold text-green-700 bg-green-100 px-3 py-1 rounded-full mb-4">
            Clinical Guide
          </span>
          <h1 className="text-4xl md:text-[44px] font-semibold text-[#1d1d1f] leading-tight tracking-tight mb-3">
            Medicare GLP-1 Bridge
          </h1>
          <div className="flex flex-wrap gap-5 text-sm text-[#86868b]">
            <span>Sources: CMS program, provider, and pharmacy materials</span>
            <span>·</span>
            <span>Source checked: June 24, 2026</span>
            <span>·</span>
            <span>Effective: July 1, 2026 – December 31, 2027</span>
          </div>
        </div>

        {/* Executive Summary */}
        <SectionTitle>Executive Summary</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
          {executiveSummary.map((item, i) => (
            <InfoCard key={i}>
              <p className="text-sm font-semibold text-[#1d1d1f] mb-2">{item.title}</p>
              <p className="text-sm text-[#3f3f46] leading-relaxed">{item.body}</p>
            </InfoCard>
          ))}
        </div>

        <TapNativeAdUnit
          adUnitId="111820"
          placement="glp1_bridge_summary"
          label="Sponsored resources"
          className="mb-2"
          minHeightClass="min-h-[120px]"
          captureIdentity
        />

        {/* June Workflow Update */}
        <SectionTitle>June 2026 Workflow Update</SectionTitle>
        <InfoCard>
          <div className="space-y-4">
            {workflowSteps.map((step) => (
              <div key={step.title} className="border-b border-[#e5e5ea] pb-4 last:border-0 last:pb-0">
                <p className="text-sm font-semibold text-[#1d1d1f]">{step.title}</p>
                <p className="mt-1 text-sm leading-relaxed text-[#3f3f46]">{step.body}</p>
              </div>
            ))}
          </div>
        </InfoCard>

        {/* What Is It */}
        <SectionTitle>What Is the Medicare GLP-1 Bridge?</SectionTitle>
        <p className="text-[15px] text-[#3f3f46] leading-relaxed mb-4">
          CMS is running a short-term demonstration that gives eligible Medicare Part D patients access to certain GLP-1 drugs for weight loss outside the normal Part D benefit flow. CMS extended the Bridge after announcing that BALANCE will not launch in Medicare in 2027.
        </p>
        <AlertBox type="warning" title="Key distinction">
          This program operates outside the normal Part D benefit flow. Part D plans do not pay or adjudicate Bridge claims.
        </AlertBox>

        {/* Key Program Details */}
        <SectionTitle>At a Glance: Key Program Details</SectionTitle>
        <Table2Col
          col1="Detail"
          col2="Answer"
          rows={programDetails.map(r => [r.detail, r.answer])}
        />

        {/* Patient Eligibility */}
        <SectionTitle>Patient Eligibility</SectionTitle>
        <p className="text-[15px] text-[#3f3f46] leading-relaxed mb-4">
          The Bridge will be nationwide and available in all states and territories. Patients must be enrolled in a standalone PDP or MA-PD plan (HMO, HMO-POS, local/regional PPO) in CY2026. This includes SNP, EGWP, LI NET, and dual-eligible enrollees in eligible plan types when PA criteria are met.
        </p>
        <InfoCard>
          <p className="text-sm font-semibold text-[#1d1d1f] mb-3">Plan types not eligible for the Bridge</p>
          <ul className="space-y-2">
            {ineligiblePlans.map((item, i) => (
              <li key={i} className="text-sm text-[#3f3f46] flex gap-2">
                <span className="text-[#86868b] mt-0.5">·</span>{item}
              </li>
            ))}
          </ul>
        </InfoCard>
        <AlertBox type="info">
          CMS notes that beneficiaries in otherwise ineligible plan types may participate if they are also enrolled in a standalone PDP, as applicable.
        </AlertBox>
        <InfoCard className="mt-4">
          <p className="text-sm font-semibold text-[#1d1d1f] mb-3">Route to Part D instead of Bridge when any of these apply</p>
          <ul className="space-y-2">
            {exclusionItems.map((item) => (
              <li key={item} className="text-sm text-[#3f3f46] flex gap-2">
                <span className="text-[#86868b] mt-0.5">·</span>{item}
              </li>
            ))}
          </ul>
        </InfoCard>

        {/* Prior Authorization */}
        <SectionTitle>Prior Authorization Criteria</SectionTitle>
        <p className="text-[15px] text-[#3f3f46] leading-relaxed mb-4">
          Patients must be age 18+ and prescribed a covered GLP-1 for weight loss in combination with structured lifestyle modification.
        </p>
        <Table2Col
          col1="BMI Threshold"
          col2="Required Condition(s)"
          rows={paCriteria.map(r => [r.threshold, r.condition])}
        />
        <AlertBox type="info" title="BMI eligibility is assessed at the time therapy was initiated — not at PA submission">
          This applies even if the patient started GLP-1 therapy before enrolling in Medicare Part D or before the July 1, 2026 Bridge launch. A patient who started therapy in 2024 with a BMI of 37 but now has a BMI of 33 still qualifies — the provider should attest to the BMI at initiation.
        </AlertBox>
        <AlertBox type="info" title="Provider enrollment">
          CMS states that a provider does not need to be enrolled in Medicare to write the prescription or submit the Bridge PA request, but the provider must not be on the CMS Preclusion List.
        </AlertBox>
        <AlertBox type="warning">
          PA requests for Bridge use should be submitted to the CMS central processor, not the patient Part D plan.
        </AlertBox>
        <AlertBox type="warning" title="PA submission prerequisite">
          CMS PA form says a denied pharmacy claim to Bridge BIN 028918 / PCN MEDDGLP1BR must exist before PA submission.
        </AlertBox>

        {/* Covered Drugs */}
        <SectionTitle>Covered Drugs</SectionTitle>
        <InfoCard className="mb-4">
          <ul className="space-y-3 text-sm text-[#3f3f46]">
            <li><span className="font-semibold text-[#1d1d1f]">Foundayo®</span> (orforglipron): all formulations</li>
            <li><span className="font-semibold text-[#1d1d1f]">Wegovy®</span> (semaglutide): all formulations — injection and tablet</li>
            <li><span className="font-semibold text-[#1d1d1f]">Zepbound®</span> (tirzepatide): <span className="font-semibold text-amber-700">KwikPen® formulation only</span> — single-dose vial and single-dose pen are not covered under the Bridge</li>
          </ul>
        </InfoCard>
        <AlertBox type="warning">
          Type 2 diabetes, moderate-to-severe obstructive sleep apnea, noncirrhotic MASH with moderate to advanced liver scarring, and cardiovascular risk reduction indications are not covered under the Bridge and should route through the patient Part D plan. CMS says Part D sponsors must continue their existing formulary exception processes and that CMS will monitor plans to ensure coverage decisions are not shifted from Part D to the Bridge.
        </AlertBox>

        <TapNativeAdUnit
          adUnitId="111819"
          placement="glp1_bridge_ndc"
          label="Sponsored resources"
          minHeightClass="min-h-[80px]"
          captureIdentity
        />

        <SectionTitle>Published NDCs</SectionTitle>
        <NdcTable rows={ndcRows} />
        <AlertBox type="info">
          CMS says this list of products and NDCs may be updated over the course of the Medicare GLP-1 Bridge.
        </AlertBox>

        {/* How Pharmacies Get Paid */}
        <SectionTitle>How Pharmacies Get Paid</SectionTitle>
        <InfoCard>
          <p className="text-sm text-[#3f3f46] leading-relaxed">
            Pharmacies collect a $50 copay at dispensing. CMS reimburses through the central processor (Humana) at wholesale acquisition cost (WAC), minus the $50 copay, plus dispensing fee and applicable sales tax. Pharmacy opt-in is not required.
          </p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <p><span className="font-semibold text-[#1d1d1f]">BIN/PCN:</span> 028918 / MEDDGLP1BR</p>
            <p><span className="font-semibold text-[#1d1d1f]">Plan/group:</span> GLP1Bridge</p>
            <p><span className="font-semibold text-[#1d1d1f]">Processor:</span> SS&C Health via RelayHealth</p>
            <p><span className="font-semibold text-[#1d1d1f]">Help desk:</span> 844-673-0910</p>
            <p><span className="font-semibold text-[#1d1d1f]">Dispensing fee:</span> $3 per claim; $5 for long-term care residents</p>
            <p><span className="font-semibold text-[#1d1d1f]">Fill quantity:</span> 28- or 30-day fills only</p>
          </div>
          <p className="mt-4 text-sm text-[#3f3f46] leading-relaxed">
            CMS states the Bridge is the primary payer for covered products, will not coordinate benefits with other payers, and will not accept paper claims or direct member reimbursements. Sixty-day, 90-day, partial-fill, and transition-fill claims are not available under the Bridge.
          </p>
        </InfoCard>

        <SectionTitle>Common Pharmacy Claim Rejects</SectionTitle>
        <RejectCodeTable rows={pharmacyTroubleshooting} />
        <AlertBox type="info">
          CMS links a detailed reject-code reference tool from the pharmacy page. Use the table here as a quick orientation, then verify claim-specific handling against the official tool and payer sheet.
        </AlertBox>

        {/* Out-of-Pocket Impact */}
        <SectionTitle>Impact on Patient Out-of-Pocket Costs</SectionTitle>
        <Table2Col
          col1="Cost Category"
          col2="Bridge Status"
          rows={oopImpact.map(r => [r.category, r.status])}
        />

        {/* 2027 Extension */}
        <SectionTitle>2027 Extension, BALANCE, and MFP Status</SectionTitle>
        <InfoCard>
          <ul className="space-y-2">
            {bridgeExtension.map((item, i) => (
              <li key={i} className="text-sm text-[#3f3f46] flex gap-2">
                <span className="text-[#86868b] mt-0.5">·</span>{item}
              </li>
            ))}
          </ul>
        </InfoCard>

        {/* Action Items */}
        <SectionTitle>Action Items for Clinicians</SectionTitle>
        <InfoCard>
          <ul className="space-y-3">
            {actionItems.map((item, i) => (
              <li key={i} className="flex gap-3 text-sm text-[#3f3f46]">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                {item}
              </li>
            ))}
          </ul>
        </InfoCard>

        {/* Reference */}
        <SectionTitle>Official CMS Sources</SectionTitle>
        <SourceLinks links={sourceLinks} />
        {/* Footer note */}
        <div className="mt-12 pt-8 border-t border-[#e5e5ea]">
          <p className="text-xs text-[#86868b]">
            Prepared by MedHug. Sources: CMS Medicare GLP-1 Bridge page last modified June 22, 2026; CMS pharmacy page last modified June 23, 2026; source checked June 24, 2026. Pharmacy billing details reference the CMS GLP-1 Bridge Payer Sheet and CMS reject-code reference tool. For clinical reference only.
          </p>
        </div>

          </article>
          <aside className="xl:sticky xl:top-24">
            <Glp1BridgeResourceNav current="guide" layout="side" />
          </aside>
        </div>
      </div>
    </div>
  );
}
